import React, { Component } from 'react';
import * as THREE from "three";
import Stats from "three/examples/jsm/libs/stats.module.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { BufferGeometryUtils } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { SpriteText2D, textAlign } from 'three-text2d';
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { SimplifyModifier } from "three/examples/jsm/modifiers/SimplifyModifier.js";
import * as TWEEN from 'es6-tween';
import { keyObjects } from './HHCanvasStuff/keyObjects';
import * as utils from './HHCanvasStuff/utils';


TWEEN.autoPlay(false);
TWEEN.ToggleLagSmoothing(true);

const style = {
	height: (window.innerHeight-64) // NOTE: The 64px is a godgiven constant toolbar height and should be replaced with something from theme.*
};

export class HHCanvas extends Component {

	componentDidMount() {
		if (!this.props.disabled) {
			this.sceneSetup();
			this.addCustomSceneObjects();
			window.addEventListener("resize", this.handleWindowResize);
			window.addEventListener("mousedown", this.handleInteract.bind(this));
			window.addEventListener("mouseup", this.handleInteract.bind(this));
			window.addEventListener("touchstart", this.handleInteract.bind(this));
			//window.addEventListener('mousemove', this.onMouseMove);
		} else {
			console.warn("Not loading HHCanvas as disabled is set to true");
		}
	}

	componentWillUnmount() {
		window.removeEventListener("resize", this.handleWindowResize);
		window.cancelAnimationFrame(this.requestID);
		if (this.controls != null) {
			this.controls.dispose();
		}
	}


	sceneSetup = () => {
		// get container dimensions and use them for scene sizing
		const width = this.el.clientWidth;
		const height = this.el.clientHeight;

		//Mouse tracing
		this.raycaster = new THREE.Raycaster();
		this.mouseCoords = new THREE.Vector2();

		//Scene
		this.scene = new THREE.Scene();
		this.scene.background = new THREE.Color( '#E6FFFB' );
		this.currentOpacity = this.props.opacity;

		//Camera
		this.camera = new THREE.PerspectiveCamera(
			55, // fov = field of view
			width / height, // aspect ratio
			0.15, // near plane
			2000 // far plane
		);

		//OrbitControls
		this.controls = new OrbitControls(this.camera, this.el);
		this.controls.target.set(10, 0, 160);
		//let newTargetPos = new THREE.Vector3(10, 0, 160);
		//let newCameraPos = new THREE.Vector3(337, 129, -20);
		this.controls.enableDamping = true;
		this.controls.dampingFactor = 0.10;
		this.controls.enableZoom = true;
		this.controls.autoRotate = true;
		this.controls.autoRotateSpeed = 0.2;
		this.controls.maxPolarAngle = 1.52; //restricts angle up/down
		this.controls.enablePan = true;
		this.controls.minDistance = 20;
		this.controls.maxDistance = 1500;
		this.controls.update();

		//Renderer
		this.renderer = new THREE.WebGLRenderer({
			antialias: true,
			powerPreference: "high-performance"
		});

		this.renderer.setPixelRatio(window.devicePixelRatio * 1); //to change resolution
		this.renderer.setSize(width, height);
		this.renderer.setClearColor(0x282828);
		this.renderer.gammaFactor = 2.2;
		this.renderer.gammaInput = true;
		this.renderer.gammaOutput = true;
		//this.renderer.shadowMap.enabled = true;
		//this.renderer.shadowMap.type = THREE.PCFSoftShadowMap; // default THREE.PCFShadowMap

		this.el.appendChild(this.renderer.domElement); // mount using React ref
		this.then = 0;

		//Stats
		if (this.props.stats) {
			this.stats = new Stats();
			this.el.appendChild(this.stats.domElement);
		}

		//Timestamp for last interaction
		this.lastInteraction = Date.now()

		//Interval until idle
		this.idleTime = 120*1000;
	};

	handleInteract = function(type){
		this.lastInteraction = Date.now();
		//console.log("Interacting: %s, lastInteraction: %s",type,this.lastInteraction);
	}

	initTexts = async function(){
		await utils.sleep(2000);
		console.log("Initializing texts");
		//Text, Object for coordinates, delta coordinates, font
		let texts = [
			["Energydisplay", "Samsung_TouchScreen_PA1-1", [0, 0.5, 0], "30px Roboto"],
			["Production: 0.00 kW", "PV_ställning-1", [-1, 2, -1], "100px Roboto"],
			["DC in: 0.00 kW, AC out: 0.00 kW", "Fronius_Symo_Assem_PA1-1", [0.6, 0.7, 0.4], "20px Roboto"],
			["422.13 m3", "VattenMätarHållare_PA1-1", [0, 0.1, 0], "20px Roboto"],
			["56325.00 kWh", "L+G_Display-1", [0, 0.2, 0.1], "20px Roboto"],
			["Status: Free | Charging: 0.00 kW", "EVSE2019_PA5-1", [0, 0.5, 0], "70px Roboto"]
		];
		texts.forEach(t => {
			let coords = new THREE.Vector3(0, 0, 0);
			utils.getObject(t[1],this.scene).getWorldPosition(coords);
			let sprite = new SpriteText2D(t[0], { align: textAlign.center, font: t[3], fillStyle: '#ff7f1e', antialias: true})
			sprite.position.set(coords.x + t[2][0], coords.y + t[2][1], coords.z + t[2][2]);
			sprite.scale.set(0.0015, 0.0015, 0.0015);
			this.scene.add(sprite);
		});
	}

	//todo, move these outside to a new file
	//change numbering to 01, 11, 34 etc. first number is the "main topic" and 2nd number is a subpage for that
	changeView = function() {
		console.log("Camera view: " + this.props.desiredView);
		let newTargetPos = new THREE.Vector3(10, 0, 160);
		let newCameraPos = new THREE.Vector3(337, 129, -20);
		let delay = 2000;
		let toTween = true;
		switch(this.props.desiredView) {
			case 0: //Overview
				this.controls.enabled = true;
				this.controls.autoRotate = true;
				this.controls.enablePan = true;
				newTargetPos = new THREE.Vector3(10, 0, 160);
				//utils.getObject("Smedsby_OSM_models",this.scene).getWorldPosition(newTargetPos);
				newCameraPos = new THREE.Vector3(337, 129, -20);
				//{x: 337.65086697859783, y: 129.09062315536931, z: -20.69877305008896}
				break;

			case 1: //Helst
				this.controls.enabled = true;
				this.controls.autoRotate = false;
				this.controls.enablePan = false;
				newCameraPos = new THREE.Vector3(151.68,54.81,36.26);
				newTargetPos = new THREE.Vector3(76.4, 20.0, -2.7);
				//{x: 76.4625897067749, y: 36.88282412700314, z: -2.702620286346672}
				//{x: 151.68232112246324, y: 54.810078014583624, z: 36.26412834070344}
				//utils.getObject("Rounded_Cylinder",this.scene).getWorldPosition(newTargetPos);
				//newTargetPos = utils.getObject("Helst",this.scene).position;
				//newCameraPos = utils.getObject("Solar_panels",this.scene).position;
				break;

			case 2: //Hoijar
				this.controls.enabled = true;
				this.controls.autoRotate = false;
				this.controls.enablePan = false;
				newCameraPos = new THREE.Vector3(-78.26,56.87,50.59);
				utils.getObject("Hoijar",this.scene).getWorldPosition(newTargetPos);
				//keyObjects.Inverter.models[0].children[1].children[0].children[0].getWorldPosition(newTargetPos);
				//newCameraPos = utils.getObject("Inverters",this.scene).position;
				break;

			case 3: //Vallonia
				this.controls.enabled = true;
				this.controls.autoRotate = false;
				this.controls.enablePan = false;
				newCameraPos = new THREE.Vector3(26.12,75.25,281.65);
				//{x: 26.12313643112799, y: 75.2535009700689, z: 281.64808877420967}
				utils.getObject("Vallonia",this.scene).getWorldPosition(newTargetPos);
				//keyObjects.Waterheater.models[0].children[4].children[0].getWorldPosition(newTargetPos);
				//newCameraPos = new THREE.Vector3(10, 15, 15);
				break;

			case 4: //Orangeriet
				this.controls.enabled = true;
				this.controls.autoRotate = false;
				this.controls.enablePan = false;
				newCameraPos = new THREE.Vector3(52.57,50.86,108.9);
				// {x: 52.56823722995422, y: 50.86714587142154, z: 108.90602935956944}
				utils.getObject("Orangeriet_PA1",this.scene).getWorldPosition(newTargetPos);
				//keyObjects.Floorheating.models[1].children[0].getWorldPosition(newTargetPos);
				//newCameraPos = utils.getObject("Solar_panels",this.scene).position;
				break;

			case 5: //Äppeldalen
				this.controls.enabled = true;
				this.controls.autoRotate = false;
				this.controls.enablePan = false;
				newCameraPos = new THREE.Vector3(111.89,78.59,381.85);
				// {x: 111.89969859825233, y: 78.59297306098523, z: 381.8538168187074}
				newTargetPos = new THREE.Vector3(137.01,20.0,501.62);
				//{x: 137.01892250639244, y: 45.28243230423676, z: 501.62893896250466}
				//utils.getObject("Appeldalen",this.scene).getWorldPosition(newTargetPos);
				//keyObjects.Ventilation.models[1].children[3].getWorldPosition(newTargetPos);
				//newCameraPos = utils.getObject("Solar_panels",this.scene).position;
				break;

			default:
				console.log("Hello you should not see this");
				this.controls.enabled = true;
				this.controls.autoRotate = true;
				this.controls.enablePan = true;
				newTargetPos = new THREE.Vector3(0, 1.2, 0);
				newCameraPos = new THREE.Vector3(-35, 18, 0);
		}

		this.currView = this.props.desiredView;

		if(toTween){
			new TWEEN.Tween(this.controls.target)
							.to(newTargetPos, delay)
							.easing(TWEEN.Easing.Circular.InOut)
							.on('update', () => {
								//console.log(this.controls.target);
								})
							.start();

			new TWEEN.Tween(this.camera.position)
							.to(newCameraPos, delay)
							.easing(TWEEN.Easing.Circular.InOut)
							.on('update', () => {
								//console.log(this.camera.position);
								})
							.start();
		}

	}

	addCustomSceneObjects = () => {
		//Lights
		let ambientLight = new THREE.AmbientLight("#fff", 0.5);
		this.scene.add(ambientLight);

		var directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
		directionalLight.position.set(300, 200, 300);
		directionalLight.target.position.set(0, 0, 0);
		//directionalLight.castShadow = true;

		var hemilight = new THREE.HemisphereLight( 0xffffbb, 0x3f678c, 0.5 );

		this.scene.add(directionalLight);
		this.scene.add(hemilight);

		//Set up shadow properties for the light
		//directionalLight.shadow.mapSize.width = 512; // default
		//directionalLight.shadow.mapSize.height = 512; // default
		//directionalLight.shadow.camera.near = 0.5; // default
		//directionalLight.shadow.camera.far = 500; // default

		//Fog
		//this.scene.fog = new THREE.Fog('#fff', 1, 100);

		//Models
		const model = 0; //lazymans model switcher for testing
		const models = [
			["../Models/gltf/","CS_Smedsby_centrum_PA1_osmupdate.glb"]
		];

		const loader = new GLTFLoader();
		const dracoLoader = new DRACOLoader();
		dracoLoader.setDecoderPath("draco/gltf/");
		loader.setDRACOLoader(dracoLoader);
		let cameraArr = [];
		let scene = this.scene;
		//let getrcolor = utils.getRandomColor;
		this.cameraArr = cameraArr;

		// mesh-decimator-3000
		const decimator = new SimplifyModifier();

		/*
		var simplified = mesh.clone();
		simplified.material = simplified.material.clone();
		simplified.material.flatShading = true;
		var count = Math.floor( simplified.geometry.attributes.position.count * 0.875 ); // number of vertices to remove
		simplified.geometry = modifier.modify( simplified.geometry, count );

		scene.add( simplified );
		*/
		// mesh-decimator-3000

		const doneLoading = () => {
			this.startAnimationLoop();
			this.controls.addEventListener( 'change', () => this.renderer.render(this.scene, this.camera));
			this.props.setLoading(false);
			//this.initKeyObjects();
			//this.initTexts();
			//this.findSolarPanels();

			//perf testing the diff meshes
			//this.cycleMainObjects();
		}
		loader.load(
			models[model][0]+models[model][1],

			function (gltf) {
				console.log("Model loading starting")
				const model = gltf.scene;
				console.log(model);
				//model.scale.set(150, 150, 150); //Different scaling for testing other 3D models
				const geomerging = false; //mainview on NUC from 25-28 fps to 45 fps (meshes from 1100+ to under 300)
				const verticemerging = false;
				const decimate = false;
				const overrideMaterial = false;
				const shadows = false;

				let meshMaterial = new THREE.MeshStandardMaterial({
					color: "#ffffff",
					transparent: false
					//side: THREE.DoubleSide,
					//opacity: props.opacity,
					//polygonOffset : true, //glPolygonOffset is useful for rendering hidden-line images, for applying decals to surfaces, and for rendering solids with highlighted edges.
					//polygonOffsetFactor : 1, // positive value pushes polygon further away
					//polygonOffsetUnits : 1,
					//wireframe: false
					//shininess: 50,
					//specular: "#F9F9F9"
				});

				const objectsToHide = [
				]

				//scalable way of doing this could be to merge all meshes/geometries into one, that aren't keyobjects/with own material
				//Could be done by traversing all objects, check if obj or its parent is in keyobjects, ignore, otherwise clump all meshes/geometries together
				//Each keyobject children that we don't need to access individually can also be merged
				if(geomerging) { //Stuff breaks a bit in current state but meshcount (ie. drawcalls) is reduced from 1000-1200 to 200-300 or so, huge improvement, in theory each mesh with same material and without specific interaction should be able to be merged into one.
					//let geometries = [];
					let objparent = model.children[0].children[0];
					console.log(objparent);
					objparent.children.forEach(child => {
						let geometries = [];
						child.traverse((o) => {
							//console.log("Checking object for children with 1+ meshes");
							//console.log(o);
							//Merge all meshes that are under one and same parent to one for starters
							if(o.children.length>0 && o.children[0].isMesh){
								//console.log("Merging meshes:");
								//console.log(o.children.length);
								o.children.forEach(mesh => {
									let geo = mesh.geometry.clone();
									geo.applyMatrix(mesh.matrix); //this works for most parts, some special cases need something else

									o.remove(mesh);
									mesh.geometry.dispose();
									mesh.material.dispose();
									mesh = undefined;

									geometries.push(geo);
								});
								/*
								let mergedGeometry = BufferGeometryUtils.mergeBufferGeometries(geometries, false);
								let newMesh = new THREE.Mesh(mergedGeometry, meshMaterial);
								o.add(newMesh);
								console.log("Meshes merged:");
								console.log(o.children.length);
								*/
							}
						});

						let mergedGeometry = BufferGeometryUtils.mergeBufferGeometries(geometries, false);
						//console.log(mergedGeometry);

						let newMesh = new THREE.Mesh(mergedGeometry, meshMaterial);
						child.add(newMesh);
					});
				}

				let meshCounter = 0;
				let savedKBytes = 0;
				model.traverse((o) => {
					//Hide heavy objects
					if(objectsToHide.includes(o.name)){
						//o.visible = false;
					}

					if (o.isMesh) {
						meshCounter++;
						//All created meshes from gltf loader should be buffergeometries, BufferGeometryUtils has some neat functions that could improve performance
						//This is done in decimate function
						if(verticemerging){
							if(o.geometry.isBufferGeometry) {
								let tolerance = 0.001;
								let before = BufferGeometryUtils.estimateBytesUsed(o.geometry);
								let newGeo = BufferGeometryUtils.mergeVertices(o.geometry, tolerance);
								let after = BufferGeometryUtils.estimateBytesUsed(newGeo);
								savedKBytes+=(before-after)/1000;
								//console.log("merged vertices, bytes before: %s | bytes after: %s", before, after);
							}
						}

						if(decimate){
							let vertices = Math.floor(o.geometry.attributes.position.count);
							let removecount = vertices * 0.1;
							let newGeo = decimator.modify(o.geometry, removecount);
							if(newGeo.attributes.position.count > 0){
								o.geometry = newGeo;
							}
							console.log("Vertices before: %s | after: %s", vertices, Math.floor(o.geometry.attributes.position.count));
						}
						if(overrideMaterial){
							o.material = meshMaterial;
						} else {
							if(o.name==="Windows"){ //Windows material turns black if opacity is used on them
								o.material.transparent = false; //Doesn't fix it
								//todo, fix other materials change colors/appearance too, e.g. the grey trims around the exterior turn white,
							} else {
								o.material.transparent = true;
							}
							o.material.polygonOffset = true;
							o.material.polygonOffsetFactor = 1;
							o.material.polygonOffsetUnits = 1;
						}
						//o.receiveShadow = false;
						//o.castShadow = false;
					} else if (o.isCamera){
						cameraArr.push(o);
						console.log(o);
					}
					if(shadows) {
						o.castShadow = true;
						o.receiveShadow = true;
					}
					//Our objects are all static for now, don't update their matrixes
					o.matrixAutoUpdate  = true;
				});
				console.log("Amount of meshes: %s | Estimated saved kbytes: %s", meshCounter, Math.round(savedKBytes));
				console.log(model);
				scene.add(model);
				doneLoading();
			},

			function (onLoad) {
				console.log("gltf loader onLoad fired");
			}
		);
	};

	startAnimationLoop = (now) => {
		let toRender = false;
		TWEEN.update();
		//console.log(this.camera.position);
		if (this.props.stats) {
			if (this.stats==null) {
				this.stats = new Stats();
				this.el.appendChild(this.stats.domElement);
			}
			this.stats.update();
		}
		this.controls.update();

		if(this.currView !== this.props.desiredView){
			this.changeView();
			this.handleInteract("ChangeView");
		}

		if(this.currentOpacity !== this.props.opacity){ //Let's not traverse all meshes every frame and update opacity even if it is unchanged.
		utils.updateModelsOpacity(this.props.opacity, keyObjects.HHBotten.models);
		utils.updateModelsOpacity(this.props.opacity, keyObjects.HHBotten_extra.models);
			this.currentOpacity = this.props.opacity;
			//Render scene in case we are not rendering (static view)
			this.renderer.render(this.scene, this.camera);
			//todo, if opacity === 1, hide internal objects (possibly add a distance check from camera to center)
		}
		//for perf testing do the rendering here also
		//this.renderer.render(this.scene, this.camera);

		//Idle state? -> Return to view 0 and/or set opacity to 1
		//lastInteraction not updating from handleInteract, does this need some promise magic or stuff?
		if(this.props.desiredView !== 0 || this.props.opacity !== 1){
			let idleAmount = Date.now()-this.lastInteraction;
			if(idleAmount > this.idleTime){
				this.props.setDesiredView(0);
				console.log("No interaction for %s seconds, resuming view 0 & opacity 1", this.idleTime/1000);
				this.lastInteraction = Date.now();
			}
		}


		if(toRender){
			this.renderer.render(this.scene, this.camera);
		}
		//console.log(this.renderer.info.render);
		this.requestID = window.requestAnimationFrame(this.startAnimationLoop);
	};

	handleWindowResize = () => {
		const width = this.el.clientWidth;
		const height = this.el.clientHeight;

		this.renderer.setSize(width, height);
		this.camera.aspect = width / height;

		// Note that after making changes to most of camera properties you have to call
		// .updateProjectionMatrix for the changes to take effect.
		this.camera.updateProjectionMatrix();
	};

	onMouseMove = (event) => {
		this.mouseCoords.x = (event.clientX / window.innerWidth ) * 2 - 1;
		this.mouseCoords.y = - (event.clientY / window.innerHeight ) * 2 + 1;
		this.raycaster.setFromCamera(this.mouseCoords, this.camera);
		let intersects = this.raycaster.intersectObjects(this.scene.children, true);
		if (intersects.length > 0) {
			console.log("Object: %s Id: %s Material: %s",intersects[0].object.name, intersects[0].object.id, intersects[0].object.material.uuid);
		}
		for ( let i = 0; i < intersects.length; i++ ) {
			//intersects[ i ].object.material.color.set( 0xff0000 );
			//console.log(intersects[i].object);
		}
	}

	render() {
		return <div style={style} ref={ref => (this.el = ref)} />;
	}
}


export default HHCanvas;